import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Checkbox, Divider, Form, Input, Select, Spin, Typography } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type { NotificationsResponse, UserSettings, UserSettingsResponse } from "../api/types";

/**
 * The personal settings of the logged-in user: name, contact data and the two
 * mail preferences.
 *
 * The credentials are deliberately absent — the login address and the password
 * go through /main/password, which demands the current password for either.
 * That split is the server's, and this screen keeps it visible rather than
 * pretending to be one big profile form.
 */
export function UserSettingsPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<UserSettings>();

  const settings = useQuery({
    queryKey: ["main", "settings"],
    queryFn: () => apiFetch<UserSettingsResponse>("/main/settings"),
  });

  useEffect(() => {
    if (settings.data) form.setFieldsValue(settings.data.data.user);
  }, [settings.data, form]);

  const update = useMutation({
    mutationFn: (values: UserSettings) =>
      apiFetch<NotificationsResponse>("/main/settings", { method: "PUT", body: values }),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey: ["main", "settings"] });
      // The name is what the header and the sidebar's own section are labelled
      // with, so both go stale with it.
      void queryClient.invalidateQueries({ queryKey: ["sidebar"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const range = settings.data?.data.delete_messages_after_months_range;

  // A select rather than a free number field, exactly like the old view: the
  // range comes from the site configuration and the server validates against it.
  const monthOptions = useMemo(() => {
    if (!range) return [];
    const options = [];
    for (let months = range.min; months <= range.max; months += 1) {
      options.push({ value: months, label: String(months) });
    }
    return options;
  }, [range]);

  if (settings.isPending) return <Spin />;

  if (settings.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={
          settings.error instanceof Error ? settings.error.message : String(settings.error)
        }
      />
    );
  }

  return (
    <Card
      title={t("navigation.user_settings")}
      style={{ maxWidth: 720 }}
      // Where the credentials went. Without this the split between the two
      // screens is something the user has to guess.
      extra={<Link to="/main/password">{t("auth.edit_account")}</Link>}
    >
      <Form<UserSettings>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => update.mutate(values)}
      >
        <Divider titlePlacement="start">
          {t("user.headers.user_data")}
        </Divider>

        <Form.Item
          name="first_name"
          label={t("activerecord.attributes.user.first_name")}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="last_name"
          label={t("activerecord.attributes.user.last_name")}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        {/* The old view grouped the contact data under "address"; keeping the
            heading keeps the screen recognisable. */}
        <Divider titlePlacement="start">
          {t("user.headers.address")}
        </Divider>

        <Form.Item name="phone" label={t("activerecord.attributes.user.phone")}>
          <Input />
        </Form.Item>

        <Form.Item name="mobile" label={t("activerecord.attributes.user.mobile")}>
          <Input />
        </Form.Item>

        <Form.Item
          name="private_email"
          label={t("activerecord.attributes.user.private_email")}
          rules={[{ type: "email" }]}
          extra={
            <Typography.Text type="secondary">{t("user.hints.private_email")}</Typography.Text>
          }
        >
          <Input />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("user.headers.settings")}
        </Divider>

        <Form.Item
          name="user_events"
          valuePropName="checked"
          extra={
            <Typography.Text type="secondary">{t("user.hints.user_events")}</Typography.Text>
          }
        >
          <Checkbox>{t("activerecord.attributes.user.user_events")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="invitation_events"
          valuePropName="checked"
          extra={
            <Typography.Text type="secondary">
              {t("user.hints.invitation_events")}
            </Typography.Text>
          }
        >
          <Checkbox>{t("activerecord.attributes.user.invitation_events")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="delete_messages_after_months"
          label={t("activerecord.attributes.user.delete_messages_after_months")}
          extra={
            <Typography.Text type="secondary">
              {t("user.hints.delete_messages_after_months")}
            </Typography.Text>
          }
        >
          <Select options={monthOptions} style={{ width: 120 }} allowClear />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={update.isPending}>
          {t("form.update")}
        </Button>
      </Form>
    </Card>
  );
}
